// @ts-nocheck
import { Post } from '../types';
import api, { API_URL } from '../api';

export const getLinkPreview = async (url: string): Promise<Post['content']['linkPreview'] | null> => {
    try {
        const domain = new URL(url).hostname.replace('www.', '');

        // YouTube
        const ytRegex = /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|shorts\/|\S*?[?&]v=)|youtu.be\/)([a-zA-Z0-9_-]{11})/;
        const ytMatch = url.match(ytRegex);
        if (ytMatch && ytMatch[1]) {
            const videoId = ytMatch[1];
            let isShorts = url.includes('/shorts/');
            let title = isShorts ? 'YouTube Short' : 'YouTube Video';
            let image = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            let isYouTubeShort = isShorts;

            try {
                const response = await api.post('metadata', { url });
                if (response.data) {
                    if (response.data.title) title = response.data.title;
                    if (response.data.image) image = response.data.image;
                    if (response.data.isYouTubeShort || response.data.url?.includes('/shorts/')) {
                        isYouTubeShort = true;
                    }
                }
            } catch (e) {
                console.warn("[getLinkPreview] Failed to fetch YouTube metadata from backend, using defaults:", e);
            }

            return {
                title: isYouTubeShort ? 'YouTube Short' : title,
                description: '',
                image: image,
                domain: 'youtube.com',
                url: url,
                embedHtml: `<iframe width="100%" height="100%" src="https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=capacitor://localhost" title="YouTube video player" frameborder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>`,
                isYouTubeShort: isYouTubeShort
            };
        }

        // Instagram
        const instaRegex = /(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel)\/([a-zA-Z0-9_-]+)/;
        const instaMatch = url.match(instaRegex);
        if (instaMatch && instaMatch[1]) {
            const mediaId = instaMatch[1];
            return {
                title: 'Instagram Post',
                description: 'View this post on Instagram.',
                image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Instagram_icon.png/600px-Instagram_icon.png',
                domain: 'instagram.com',
                url: url,
                embedHtml: `<iframe src="https://www.instagram.com/p/${mediaId}/embed/" width="100%" height="450" frameborder="0" scrolling="no" allowtransparency="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>`
            };
        }

        // Facebook
        if (url.includes('facebook.com') || url.includes('fb.watch')) {
            let title = 'Facebook Post';
            let description = 'View this content on Facebook.';
            let image = 'https://upload.wikimedia.org/wikipedia/commons/b/b8/2021_Facebook_icon.svg';

            let video = '';
            try {
                const response = await api.post('metadata', { url });
                if (response.data) {
                    if (response.data.title) title = response.data.title;
                    if (response.data.description) description = response.data.description;
                    if (response.data.image) image = response.data.image;
                    if (response.data.video) video = response.data.video;
                }
            } catch (e) {
                console.warn("[getLinkPreview] Failed to fetch Facebook metadata from backend, using defaults:", e);
            }

            return {
                title: title,
                description: description,
                image: image,
                domain: 'facebook.com',
                url: url,
                embedHtml: `<iframe src="${API_URL}facebook-proxy?url=${encodeURIComponent(url)}" width="100%" height="500" style="border:none;overflow:hidden" scrolling="no" frameborder="0" allowfullscreen="true" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>`,
                video: video
            };
        }

        // TikTok
        const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/.*\/video\/(\d+)/;
        
        if (url.includes('tiktok.com')) {
            let videoId = '';
            let finalUrl = url;
            
            const tiktokMatch = url.match(tiktokRegex);
            if (tiktokMatch && tiktokMatch[1]) {
                videoId = tiktokMatch[1];
            } else {
                // If it's a short URL, resolve it using our backend metadata endpoint
                try {
                    const response = await api.post('metadata', { url });
                    if (response.data && response.data.url) {
                        finalUrl = response.data.url;
                        const resolvedMatch = finalUrl.match(tiktokRegex);
                        if (resolvedMatch && resolvedMatch[1]) {
                            videoId = resolvedMatch[1];
                        }
                    }
                } catch (e) {
                    console.error("Failed to resolve short TikTok URL:", e);
                }
            }

            if (videoId) {
                return {
                    title: 'TikTok Video',
                    description: 'Watch this video on TikTok.',
                    image: 'https://sf-static.tiktokcdn.com/obj/tiktok-web/tiktok/web/node/_next/static/images/logo-dark-e95da587b6efa1520d8f331b11f4d36d.svg',
                    domain: 'tiktok.com',
                    url: finalUrl,
                    embedHtml: `<iframe src="${API_URL}tiktok-proxy?videoId=${videoId}" width="100%" height="100%" frameborder="0" allowfullscreen allow="encrypted-media; autoplay" scrolling="no"></iframe>`
                };
            }
        }

        // SoundCloud
        const soundcloudRegex = /(https?:\/\/(?:www\.|m\.|on\.)?soundcloud\.com\/[a-zA-Z0-9\/\-\_]+(?:\?[a-zA-Z0-9\=\&\-\_]+)?)/;
        
        if (url.includes('soundcloud.com')) {
            let finalUrl = url;
            
            // If it's a short URL, try to resolve it via backend first to get the real track URL
            if (url.includes('on.soundcloud.com')) {
                try {
                    const response = await api.post('metadata', { url });
                    if (response.data && response.data.url) {
                        finalUrl = response.data.url;
                    }
                } catch (e) {
                    console.error("Failed to resolve short SoundCloud URL:", e);
                }
            }

            const soundcloudMatch = finalUrl.match(soundcloudRegex);
            if (soundcloudMatch) {
                const soundcloudUrl = soundcloudMatch[0];
                return {
                    title: 'SoundCloud',
                    description: 'Listen on SoundCloud.',
                    domain: 'soundcloud.com',
                    url: finalUrl,
                    embedHtml: `<iframe width="100%" height="166" scrolling="no" frameborder="no" allow="autoplay" src="https://w.soundcloud.com/player/?url=${encodeURIComponent(soundcloudUrl)}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_bumps=false&show_teaser=true&visual=true"></iframe>`
                };
            }
        }

        // Apple Music
        const appleMusicRegex = /(https?:\/\/music\.apple\.com\/[^\s]+)/;
        const appleMusicMatch = url.match(appleMusicRegex);
        if (appleMusicMatch) {
            const embedUrl = appleMusicMatch[0].replace('music.apple.com', 'embed.music.apple.com');
            return {
                title: 'Apple Music',
                description: 'Listen on Apple Music.',
                domain: 'music.apple.com',
                url: url,
                embedHtml: `<iframe allow="autoplay *; encrypted-media *; fullscreen *" frameborder="0" height="175" style="width:100%;max-width:660px;overflow:hidden;background:transparent;" sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" src="${embedUrl}"></iframe>`
            };
        }

        // Spotify
        const spotifyRegex = /(?:https?:\/\/)?(?:open\.)?spotify\.com\/(track|playlist|album|artist|show|episode)\/([a-zA-Z0-9]+)/;
        const spotifyMatch = url.match(spotifyRegex);
        if (spotifyMatch && spotifyMatch[1] && spotifyMatch[2]) {
            const type = spotifyMatch[1];
            const id = spotifyMatch[2];
            const height = (type === 'track' || type === 'episode') ? 152 : 352;
            const title = `Spotify ${type.charAt(0).toUpperCase() + type.slice(1)}`;
            return {
                title,
                description: `Listen to this ${type} on Spotify.`,
                image: 'https://storage.googleapis.com/pr-newsroom-wp/1/2018/11/Spotify_Logo_RGB_Green.png',
                domain: 'spotify.com',
                url: url,
                embedHtml: `<iframe style="border-radius:12px" src="https://open.spotify.com/embed/${type}/${id}?utm_source=generator" width="100%" height="${height}" frameBorder="0" allowfullscreen="" allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy"></iframe>`
            };
        }

        // Reddit
        if (url.includes('reddit.com') || url.includes('redd.it')) {
            const redditCleanUrl = url.split('?')[0].replace(/\/$/, '');
            return {
                title: 'Reddit Discussion',
                description: 'View thread & comments on Reddit.',
                image: 'https://www.redditstatic.com/shreddit/assets/favicon/192x192.png',
                domain: 'reddit.com',
                url: url,
                embedHtml: `<iframe src="https://www.redditmedia.com${redditCleanUrl.replace(/https?:\/\/(www\.)?reddit\.com/, '')}?ref_source=embed&amp;ref=share&amp;embed=true" width="100%" height="480" scrolling="no" frameborder="0"></iframe>`
            };
        }

        // Threads
        if (url.includes('threads.net')) {
            return {
                title: 'Threads Post',
                description: 'View post on Threads.',
                image: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9d/Threads_%28app%29_logo.svg/600px-Threads_%28app%29_logo.svg.png',
                domain: 'threads.net',
                url: url,
                embedHtml: `<iframe src="${url.replace(/\/$/, '')}/embed" width="100%" height="450" frameborder="0" scrolling="no"></iframe>`
            };
        }

        // Pinterest
        const pinterestMatch = url.match(/(?:https?:\/\/)?(?:www\.)?pinterest\.[a-z.]+\/pin\/(\d+)/);
        if (pinterestMatch && pinterestMatch[1]) {
            const pinId = pinterestMatch[1];
            return {
                title: 'Pinterest Pin',
                description: 'View Pin on Pinterest.',
                image: 'https://upload.wikimedia.org/wikipedia/commons/0/08/Pinterest-logo.png',
                domain: 'pinterest.com',
                url: url,
                embedHtml: `<iframe src="https://assets.pinterest.com/ext/embed.html?id=${pinId}" height="520" width="345" frameborder="0" scrolling="no"></iframe>`
            };
        }

        // Generic fallback - fetch metadata from backend to avoid CORS issues
        try {
            const response = await api.post('metadata', { url });
            const metadata = response.data;
            console.log("Fetched metadata from backend:", metadata);
            return {
                title: metadata.title || domain,
                description: metadata.description || '',
                image: metadata.image || undefined, // Don't fallback to favicon here, let UI handle it
                domain: domain,
                url: url
            };
        } catch (fetchError: any) {
            console.error("Failed to fetch metadata from backend:", fetchError.message);
        }

        // Fallback if metadata fetch fails
        return {
            title: domain,
            description: url,
            image: undefined,
            domain: domain,
            url: url
        };

    } catch (e) {
        console.error("Invalid URL for preview:", e);
        return null;
    }
};
